"use client";

import Image from "next/image";
import { useProductTeasers } from "@/hooks/useProductTeasers";
import Link from "next/link";
import { AnimatePresence, motion, PanInfo } from "framer-motion";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  HeadingBlock,
  ProductCardBlock,
  findBlocks,
  normalizeProductList,
  type ProductListItem,
} from "./blockUtils";
import type { ContentTeaserComponent } from "./types";
import { useQuery } from "@tanstack/react-query";
import type { Locale } from "@/cms/blocks/registry/types";
import type {
  ProductClientState,
  ProductPropertyWithMeta,
} from "@/zod/product.schema";
import { MediaAssetDto } from "@/media/types";
import type { ProductTeaser } from "@/types/productTeaser";
import clsx from "clsx";
import arrow2 from "@/../public/assets/images/arrow2.svg";
import { ArrowSVG, EditSVG, FarbinPlaceholderSVG } from "@/components/ui/SVG";
import { X } from "lucide-react";
import { EntityView } from "@/analytics/useEntityView";

type RtlScrollBehavior =
  "negative" | "positive-descending" | "positive-ascending";

let cachedRtlScrollBehavior: RtlScrollBehavior | null = null;

const getRtlScrollBehavior = (): RtlScrollBehavior => {
  if (cachedRtlScrollBehavior) return cachedRtlScrollBehavior;
  if (typeof document === "undefined") return "positive-ascending";

  const scrollContainer = document.createElement("div");
  scrollContainer.dir = "rtl";
  scrollContainer.style.width = "50px";
  scrollContainer.style.height = "50px";
  scrollContainer.style.overflow = "scroll";
  scrollContainer.style.position = "absolute";
  scrollContainer.style.top = "-9999px";

  const inner = document.createElement("div");
  inner.style.width = "100px";
  inner.style.height = "100px";
  scrollContainer.appendChild(inner);
  document.body.appendChild(scrollContainer);

  scrollContainer.scrollLeft = 0;
  const initial = scrollContainer.scrollLeft;
  scrollContainer.scrollLeft = 1;

  if (scrollContainer.scrollLeft === 0) {
    cachedRtlScrollBehavior = "negative";
  } else if (initial === 0) {
    cachedRtlScrollBehavior = "positive-ascending";
  } else {
    cachedRtlScrollBehavior = "positive-descending";
  }

  document.body.removeChild(scrollContainer);
  return cachedRtlScrollBehavior;
};

const getMaxScrollLeft = (el: HTMLElement) =>
  Math.max(0, el.scrollWidth - el.clientWidth);

const getNormalizedScrollLeft = (el: HTMLElement, isRTL: boolean) => {
  const max = getMaxScrollLeft(el);
  if (!isRTL) return Math.min(Math.max(0, el.scrollLeft), max);

  const behavior = getRtlScrollBehavior();
  switch (behavior) {
    case "negative":
      return max + el.scrollLeft;
    case "positive-descending":
      return max - el.scrollLeft;
    case "positive-ascending":
    default:
      return el.scrollLeft;
  }
};

const setNormalizedScrollLeft = (
  el: HTMLElement,
  value: number,
  isRTL: boolean,
) => {
  const max = getMaxScrollLeft(el);
  const next = Math.min(Math.max(0, value), max);
  if (!isRTL) {
    el.scrollLeft = next;
    return;
  }

  const behavior = getRtlScrollBehavior();
  switch (behavior) {
    case "negative":
      el.scrollLeft = next - max;
      break;
    case "positive-descending":
      el.scrollLeft = max - next;
      break;
    case "positive-ascending":
    default:
      el.scrollLeft = next;
      break;
  }
};

type ProductPropertyItem = NonNullable<
  ProductClientState["productProperty"]
>[number];

type ProductWithAssets = ProductClientState & {
  heroAsset?: MediaAssetDto | null;
  categoryAsset?: MediaAssetDto | null;
};

function isPropertyWithMeta(
  item: ProductPropertyItem,
): item is ProductPropertyWithMeta {
  return Boolean(
    item && typeof item === "object" && "property" in item && "option" in item,
  );
}

const getLocalizedText = (
  locale: Locale,
  en?: string | null,
  fa?: string | null,
) => {
  const primary = locale === "fa" ? fa : en;
  const fallback = locale === "fa" ? en : fa;
  return primary ?? fallback ?? null;
};

const getOptionLabel = (
  locale: Locale,
  option: ProductPropertyWithMeta["option"],
) => {
  return (
    getLocalizedText(locale, option.value_en, option.value_fa) ??
    option.value_code
  );
};

/**
 * Wrapper that handles:
 * - Expand/collapse to fullscreen
 * - Body scroll lock
 * - Overlay fade in/out
 * - Layout animations + placeholder height
 * - Keyboard accessibility (Enter/Space, Escape)
 */
type ExpandableTeaserCardProps = {
  canExpand: boolean;
  children: (ctx: {
    isExpanded: boolean;
    handleCollapse: () => void;
  }) => ReactNode;
  className?: string;
  expandedMaxWidth?: number | string;
  expandedHeight?: number | string;
  expandedAlignment?: "start" | "center";
  /**
   * Optional value to trigger recomputing collapsed height when content changes.
   * E.g. pass products.length or some version key.
   */
  recalcOn?: unknown;
  /**
   * Enable swipe-down-to-close gesture when expanded.
   * Use this in carousel mode only.
   */
  enableSwipeToClose?: boolean;
  /**
   * How far the user must drag down (in px) to trigger close.
   * Only used when enableSwipeToClose is true.
   */
  swipeCloseThreshold?: number;
  /**
   * Region where swipe can start.
   * - "top-half": only swipes that begin in the top half of the viewport
   * - "full": swipes from anywhere
   */
  swipeActiveRegion?: "top-half" | "full";
};

const ExpandableTeaserCard: React.FC<ExpandableTeaserCardProps> = ({
  canExpand,
  children,
  className = "",
  expandedMaxWidth = "calc(100% - 32px)",
  expandedHeight = "89vh",
  expandedAlignment = "start",
  recalcOn,
  enableSwipeToClose = false,
  swipeCloseThreshold = 120,
  swipeActiveRegion = "full",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const articleRef = useRef<HTMLDivElement | null>(null);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const dragAllowedRef = useRef(false);

  const isInteractive = canExpand && !isExpanded;
  const isFixedView = isExpanded;
  const alignExpandedToCenter = expandedAlignment === "center";

  // Measure collapsed height so we can reserve space when expanded
  useLayoutEffect(() => {
    if (!isExpanded && articleRef.current) {
      setCollapsedHeight(articleRef.current.getBoundingClientRect().height);
    }
  }, [isExpanded, recalcOn]);

  // Lock body scroll + close on Escape when in fixed mode
  useEffect(() => {
    if (!isFixedView) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExpanded(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFixedView]);

  const handleExpand = () => {
    if (!canExpand || isExpanded) return;
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    dragAllowedRef.current = false;
  };

  const handleDragStart = (
    event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    if (!enableSwipeToClose || !isFixedView) {
      dragAllowedRef.current = false;
      return;
    }

    const startY = info.point.y;
    const viewportHeight = window.innerHeight;

    if (swipeActiveRegion === "top-half") {
      dragAllowedRef.current = startY <= viewportHeight / 2;
    } else {
      dragAllowedRef.current = true;
    }
  };

  const handleDragEnd = (
    event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    if (!enableSwipeToClose || !isFixedView) return;
    if (!dragAllowedRef.current) return;

    if (info.offset.y > swipeCloseThreshold) {
      handleCollapse();
    }
  };

  const content = children({ isExpanded, handleCollapse });

  return (
    <>
      {/* Placeholder to keep layout from jumping when we go fixed/fullscreen */}
      {isExpanded && (
        <div
          aria-hidden="true"
          style={{
            height: isFixedView ? (collapsedHeight ?? 0) : 0,
            transition: "height 0.3s ease",
          }}
        />
      )}

      {/* Dimmed background overlay */}
      <AnimatePresence>
        {isExpanded ? (
          <motion.div
            key="expandable-card-overlay"
            className="fixed inset-0 z-900 bg-black/20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleCollapse}
          />
        ) : null}
      </AnimatePresence>

      {/* Main card */}
      <motion.article
        layout
        transition={{
          type: "spring",
          stiffness: 430,
          damping: 30,
          mass: 1,
        }}
        ref={articleRef}
        className={`transform rounded-lg select-none ${
          isInteractive ? "cursor-pointer" : ""
        } ${
          isFixedView
            ? `fixed inset-0 z-1000 mx-auto flex w-full flex-col justify-center ${
                alignExpandedToCenter ? "items-center" : "items-start"
              }`
            : ""
        } ${className}`}
        style={{
          height: isFixedView ? expandedHeight : undefined,
          maxWidth: isFixedView ? expandedMaxWidth : undefined,
          width: isFixedView ? "95%" : undefined,
        }}
        whileTap={isInteractive ? { scale: 0.95 } : undefined}
        onTap={isInteractive ? handleExpand : undefined}
        onKeyDown={
          isInteractive
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleExpand();
                }
              }
            : undefined
        }
        role={canExpand ? "button" : undefined}
        aria-expanded={canExpand ? isExpanded : undefined}
        tabIndex={canExpand ? 0 : undefined}
        // Swipe-down-to-close gesture (only when expanded + enabled)
        drag={isFixedView && enableSwipeToClose ? "y" : false}
        dragElastic={0.2}
        dragMomentum={false}
        dragSnapToOrigin
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {content}
      </motion.article>
    </>
  );
};

type TeaserProductRowProps = {
  product: ProductListItem;
  isExpanded: boolean;
  prefetchedProduct?: ProductTeaser;
  variant?: "list" | "single";
  locale: Locale;
  copy: {
    loading: string;
    error: string;
  };
  headingLabel?: string | null;
};

const FALLBACK_IMAGE = "/assets/images/px.jpg";

const TeaserProductRow: React.FC<TeaserProductRowProps> = ({
  product,
  isExpanded,
  prefetchedProduct,
  variant = "list",
  locale,
  copy,
  headingLabel,
}) => {
  const isRTL = locale === "fa";
  const productId = product.productId;

  const shouldFetchOnExpand = Boolean(
    isExpanded && productId && !prefetchedProduct,
  );

  const productQuery = useQuery({
    queryKey: ["product", productId],
    queryFn: async (): Promise<ProductWithAssets | null> => {
      if (!productId) return null;
      const res = await fetch(`/api/products/${productId}`);
      if (!res.ok) throw new Error("Failed to load product");
      const json = await res.json();
      return json.product as ProductWithAssets;
    },
    enabled: shouldFetchOnExpand && Boolean(productId),
    staleTime: 300_000,
  });

  const fullProduct = prefetchedProduct ?? productQuery.data ?? null;
  const isLoadingProduct = productQuery.isPending && shouldFetchOnExpand;
  const isErrorProduct = productQuery.isError;

  const partNumber =
    fullProduct?.part_number ??
    product.partNumber ??
    product.productId ??
    "Product";
  const descriptionTitle = getLocalizedText(
    locale,
    fullProduct?.description_title_en,
    fullProduct?.description_title_fa,
  );
  const descriptionText = getLocalizedText(
    locale,
    fullProduct?.description_text_en,
    fullProduct?.description_text_fa,
  );
  const slug =
    fullProduct?.slug ??
    fullProduct?.part_number ??
    product.partNumber ??
    product.productId ??
    "";
  const href = slug
    ? `/${locale}/product/${encodeURIComponent(slug)}`
    : undefined;

  const imgSrc = fullProduct?.categoryAsset?.url || fullProduct?.category_image;

  const imgSrcSingle = imgSrc;

  const properties = (fullProduct?.productProperty ?? []).filter(
    isPropertyWithMeta,
  );
  const isSingleVariant = variant === "single";
  const headingBadge =
    typeof headingLabel === "string" && headingLabel.trim().length > 0
      ? headingLabel
      : null;
  const imageSize = isSingleVariant ? 500 : isExpanded ? 88 : 64;
  const imageClass = isSingleVariant ? "w-full" : "w-26 lg:w-30";

  const body = isSingleVariant ? (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={clsx(
        "flex w-full flex-col rounded-lg bg-white sm:items-center lg:h-full",
      )}
    >
      <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-white">
        {headingBadge ? (
          <span
            className={clsx(
              "bg-grayCustom absolute top-0 z-10 px-3 py-1 text-sm font-semibold",
              isRTL
                ? "-right-1 rounded-bl-lg rtl:pl-6"
                : "-left-1 rounded-br-lg ltr:pr-6",
            )}
          >
            {headingBadge}
          </span>
        ) : null}
        {imgSrcSingle ? (
          <Image
            src={imgSrcSingle}
            alt={partNumber}
            width={imageSize}
            height={Math.round((imageSize * 9) / 16)}
            className={`${imageClass} h-full rounded-xl object-cover`}
          />
        ) : (
          <FarbinPlaceholderSVG className="h-2/3 w-2/3" />
        )}
      </div>
      <div className="flex flex-col space-y-2 border-t border-gray-200 pt-4 ltr:ml-4">
        <span
          dir="ltr"
          className="bg-white/15 text-[19px] font-bold tracking-wide text-gray-900 backdrop-blur-xs lg:text-2xl"
        >
          {partNumber}
        </span>
        {descriptionText ? (
          <p className="rtl:mr-4">
            <span className="mb-2 block text-[15px] font-semibold tracking-wide lg:text-lg rtl:text-right">
              {descriptionTitle}
            </span>
            <span className="lg block text-sm text-gray-700 lg:text-base ltr:pr-4 ltr:leading-6 lg:ltr:leading-7 lg:ltr:tracking-wide rtl:pr-0 rtl:pl-4 rtl:text-justify rtl:leading-7 rtl:tracking-tight lg:rtl:leading-9">
              {descriptionText}
            </span>
          </p>
        ) : null}
        <div
          className={clsx(
            "flex flex-wrap items-center gap-2 divide-x divide-gray-300 text-xs text-gray-700",
            isRTL && "divide-x-reverse",
          )}
        >
          {isLoadingProduct && properties.length === 0 && !prefetchedProduct ? (
            <span className={clsx("text-gray-500", isRTL ? "pl-2" : "pr-2")}>
              {copy.loading}
            </span>
          ) : null}
          {/* {properties.map((p) => {
            const { property, option, optionId, propertyId } = p;
            const key = `${propertyId}-${optionId}`;
            return (
              <span className="pr-2" key={key}>
                {property.kind === "COLOR" ? (
                  <span className="flex items-center space-x-1">
                    <span>{option.value_en ?? option.value_code}</span>
                    <span
                      className="inline-block h-4 w-4 rounded-xs"
                      style={{ backgroundColor: option.hex ?? "" }}
                    />
                  </span>
                ) : (
                  (option.value_code ?? option.value_en ?? option.value_fa)
                )}
              </span>
            );
          })} */}
        </div>
        {isErrorProduct ? (
          <span
            className={clsx(
              "text-xs text-red-600",
              isRTL ? "text-right" : undefined,
            )}
          >
            {copy.error}
          </span>
        ) : null}
      </div>
    </div>
  ) : (
    <div dir="ltr" className="flex w-full gap-x-2 py-3 pr-2 lg:gap-x-5">
      <div
        className={`${imageClass} flex aspect-video shrink-0 items-center justify-center self-start overflow-hidden rounded-md`}
      >
        {imgSrc ? (
          <Image
            src={imgSrc}
            alt={partNumber}
            width={imageSize}
            height={Math.round((imageSize * 9) / 16)}
            className="bg-grayCustom h-full w-full object-cover"
          />
        ) : (
          <FarbinPlaceholderSVG className="h-2/3 w-2/3" />
        )}
      </div>

      <div className={clsx("flex min-w-0 flex-1 flex-col pt-1")}>
        <span className="text-left text-sm font-semibold text-gray-900 lg:text-lg">
          {partNumber}
        </span>

        <div
          className={clsx(
            "flex w-fit flex-wrap items-center gap-1.5 gap-y-2 pt-3 pr-2 text-gray-700 lg:pt-0",
          )}
        >
          {isLoadingProduct && properties.length === 0 && !prefetchedProduct ? (
            <span className={clsx("text-gray-500")}>{copy.loading}</span>
          ) : null}
          {properties.map((p) => {
            const { property, option, optionId, propertyId } = p;
            const key = `${propertyId}-${optionId}`;
            return (
              <span
                className={clsx(
                  "border-r border-gray-300 text-xs last:border-r-0 lg:text-sm ltr:pr-1.5 lg:rtl:-translate-x-1.5",
                )}
                key={key}
                dir={locale === "fa" ? "rtl" : "ltr"}
              >
                {property.kind === "COLOR" ? (
                  <span className="flex items-center space-x-1 lg:space-x-1.5">
                    <span>{getOptionLabel(locale, option)}</span>
                    <span
                      className="inline-block h-4 w-4 rounded-xs lg:-translate-y-0.5"
                      style={{ backgroundColor: option.hex ?? "" }}
                    />
                  </span>
                ) : (
                  getOptionLabel(locale, option)
                )}
              </span>
            );
          })}
        </div>

        {status === "error" ? (
          <span
            className={clsx(
              "text-xs text-red-600",
              isRTL ? "text-right" : undefined,
            )}
          >
            {copy.error}
          </span>
        ) : null}
      </div>
    </div>
  );

  return (
    <motion.li
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
      }}
      className="list-none lg:h-full"
    >
      {href && isExpanded ? (
        <Link className="block lg:h-full" href={href}>
          {body}
        </Link>
      ) : (
        body
      )}
    </motion.li>
  );
};

type ProductCarouselProps = {
  heading?: HeadingBlock;
  titleText?: string | null;
  contentId?: string | null;
  onRequestEdit?: (contentId: string) => void;
  showEditButton?: boolean;
  products: ProductListItem[];
  prefetchedProducts: Record<string, ProductTeaser>;
  locale: Locale;
};

function ProductCarousel({
  heading,
  titleText,
  contentId,
  onRequestEdit,
  showEditButton,
  products,
  prefetchedProducts,
  locale,
}: ProductCarouselProps) {
  const isRTL = locale === "fa";
  const canShowEdit = Boolean(showEditButton && contentId && onRequestEdit);
  const headingLabel = titleText ?? heading?.data?.text ?? null;
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const max = getMaxScrollLeft(el);
    if (max <= 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const current = getNormalizedScrollLeft(el, isRTL);
    const epsilon = 2;

    setCanScrollLeft(current > epsilon);
    setCanScrollRight(current < max - epsilon);
  }, [isRTL]);

  useEffect(() => {
    updateScrollButtons();

    const el = scrollerRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollButtons, { passive: true });
    const onResize = () => updateScrollButtons();
    window.addEventListener("resize", onResize);

    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", onResize);
    };
  }, [products.length, updateScrollButtons]);

  const scrollByCards = (direction: "prev" | "next") => {
    const el = scrollerRef.current;
    if (!el) return;

    // Scroll by ~2 cards (desktop) while remaining responsive on smaller widths.
    const delta = Math.max(240, Math.floor(el.clientWidth * 0.25));
    const current = getNormalizedScrollLeft(el, isRTL);
    const next = direction === "prev" ? current - delta : current + delta;
    const clamped = Math.min(Math.max(0, next), getMaxScrollLeft(el));

    // Preserve smooth scrolling across browsers (including RTL oddities).
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (prefersReduced) {
      setNormalizedScrollLeft(el, clamped, isRTL);
      updateScrollButtons();
      return;
    }

    // Use an animation loop for smoothness while setting normalized scrollLeft.
    const start = current;
    const change = clamped - start;
    const durationMs = 0;
    const startTime = performance.now();

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (now: number) => {
      const t = Math.min(1, (now - startTime) / durationMs);
      const value = start + change * easeOutCubic(t);
      setNormalizedScrollLeft(el, value, isRTL);
      if (t < 1) requestAnimationFrame(step);
      else updateScrollButtons();
    };

    requestAnimationFrame(step);
  };

  return (
    <section className="rounded-xl">
      <div
        className="flex items-center justify-between gap-3"
        dir={isRTL ? "rtl" : "ltr"}
      >
        {headingLabel ? (
          <h2 className="relative flex w-full justify-between text-xl font-bold text-gray-900 lg:mx-0 lg:text-2xl ltr:ml-4 rtl:mr-4">
            {headingLabel}
            {canShowEdit ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onRequestEdit!(contentId!);
                }}
                className="mr-2 inline-flex h-7 w-7 items-center justify-center rounded-md align-middle"
                aria-label="Edit content"
                title="Edit content"
              >
                <EditSVG
                  className="h-5 w-5"
                  svgColor="pinkCustom"
                  accentColor="var(--color-gray-500)"
                />
              </button>
            ) : null}
            <div className={`absolute top-5 ltr:-left-3.5 rtl:-right-3.5`}>
              <Image className="w-3 rtl:-rotate-90" src={arrow2} alt="arrow" />
            </div>
          </h2>
        ) : (
          <span className="text-sm font-medium text-gray-700">
            {isRTL ? "محصولات" : "Products"}
          </span>
        )}
      </div>

      <div className="relative mt-4">
        {/* Desktop-only arrows; only visible when scrolling is possible */}
        {canScrollLeft ? (
          <button
            type="button"
            aria-label={isRTL ? "Scroll right" : "Scroll left"}
            onClick={() => scrollByCards("prev")}
            className={clsx(
              "hidden opacity-0 lg:flex",
              "absolute top-1/2 left-0 z-10 -translate-y-1/2 items-center justify-center",
              "h-full w-16 bg-linear-to-r from-gray-200 to-transparent duration-200 hover:opacity-100",
              "cursor-pointer text-gray-700",
            )}
          >
            <span
              className={clsx(
                "block text-lg leading-none",
                isRTL && "rotate-180",
              )}
            >
              <ArrowSVG
                className="h-6 w-6 rotate-180"
                svgColor="var(--color-gray-400)"
              />
            </span>
          </button>
        ) : null}
        {canScrollRight ? (
          <button
            type="button"
            aria-label={isRTL ? "Scroll left" : "Scroll right"}
            onClick={() => scrollByCards("next")}
            className={clsx(
              "hidden opacity-0 lg:flex",
              "absolute top-1/2 right-0 z-10 -translate-y-1/2 items-center justify-center",
              "h-full w-16 bg-linear-to-l from-gray-200 to-transparent duration-200 hover:opacity-100",
              "cursor-pointer text-gray-700",
            )}
          >
            <span
              className={clsx(
                "block text-lg leading-none",
                isRTL && "rotate-180",
              )}
            >
              <ArrowSVG className="h-6 w-6" svgColor="var(--color-gray-400)" />
            </span>
          </button>
        ) : null}
        <div
          ref={scrollerRef}
          className="overflow-x-auto scroll-smooth pb-2"
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div className="flex gap-3">
            {products.map((product, index) => {
              const prefetchedProduct = product.productId
                ? prefetchedProducts[product.productId]
                : undefined;

              return (
                <CarouselProductCard
                  key={
                    product.productId ??
                    product.partNumber ??
                    `carousel-${index}`
                  }
                  product={product}
                  locale={locale}
                  prefetchedProduct={prefetchedProduct}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function CarouselProductCard({
  product,
  locale,
  prefetchedProduct,
}: {
  product: ProductListItem;
  locale: Locale;
  prefetchedProduct?: ProductTeaser;
}) {
  const isRTL = locale === "fa";
  const fullProduct = prefetchedProduct ?? null;

  const partNumber =
    fullProduct?.part_number ??
    product.partNumber ??
    product.productId ??
    "Product";

  const title = getLocalizedText(
    locale,
    fullProduct?.description_title_en,
    fullProduct?.description_title_fa,
  );

  const description = getLocalizedText(
    locale,
    fullProduct?.description_text_en,
    fullProduct?.description_text_fa,
  );

  const slug =
    fullProduct?.slug ??
    fullProduct?.part_number ??
    product.partNumber ??
    product.productId ??
    "";
  const href = slug
    ? `/${locale}/product/${encodeURIComponent(slug)}`
    : undefined;

  const properties = (fullProduct?.productProperty ?? []).filter(
    isPropertyWithMeta,
  );

  const imgSrc = fullProduct?.heroAsset?.url || fullProduct?.hero_image;

  const closeLabel = locale === "fa" ? "بستن" : "Close";
  const viewLabel = locale === "fa" ? "مشاهده محصول" : "View product";

  const cardClass = "flex flex-col overflow-hidden bg-white top-18 lg:top-10";

  return (
    <div className="flex h-full w-65 flex-none lg:w-85">
      <ExpandableTeaserCard
        canExpand={true}
        className={cardClass}
        recalcOn={product.productId}
        expandedAlignment="center"
        expandedHeight="fit-content"
        expandedMaxWidth={window.innerWidth > 1024 ? "700px" : "100% - 32px"}
      >
        {({ isExpanded, handleCollapse }) => (
          <div
            className="relative flex flex-1 flex-col"
            dir={isRTL ? "rtl" : "ltr"}
          >
            <EntityView
              type="product"
              id={product.productId}
              url={href}
              open={isExpanded && Boolean(fullProduct?.slug)}
            />
            {isExpanded ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleCollapse();
                }}
                className="absolute top-4 right-4 z-20   bg-grayCustom text-slateCustom hover:text-pinkCustom flex h-9 w-9 items-center justify-center rounded-full transition"
              >
                <X className="h-4 w-4" strokeWidth={4} />
              </button>
            ) : null}
            <div className="relative aspect-video w-full overflow-hidden bg-white">
              {imgSrc ? (
                <Image
                  src={imgSrc}
                  alt={partNumber}
                  width={320}
                  height={180}
                  className="h-full w-full object-cover"
                />
              ) : (
                <FarbinPlaceholderSVG className="h-full w-full p-10" />
              )}
            </div>

            {!isExpanded && (
              <hr className="ml-3 h-px w-[calc(100%-12px)] bg-gray-200 text-gray-200" />
            )}

            <div className="flex flex-1 flex-col gap-2 p-3 px-0 lg:px-3">
              {/* Part number */}
              <p
                className={clsx(
                  "pl-3 font-bold text-gray-900",
                  isExpanded
                    ? "text-xl"
                    : "text-[14px] tracking-[0.0125em] lg:text-base",
                )}
                dir="ltr"
              >
                {partNumber}
              </p>

              {isExpanded && (
                <div
                  className={clsx(
                    "flex flex-wrap items-center divide-x divide-gray-300 text-gray-700 ltr:gap-y-3 ltr:pr-0 ltr:pl-6 rtl:gap-y-3 rtl:pr-6 rtl:pl-0",
                    isExpanded ? "gap-2 text-xs" : "gap-2 text-[11px]",
                  )}
                >
                  {properties.map((p) => {
                    const { property, option, optionId, propertyId } = p;
                    const key = `${propertyId}-${optionId}`;
                    return (
                      <span
                        className={clsx(isRTL ? "pl-2" : "pr-2")}
                        dir={isRTL ? "rtl" : "ltr"}
                        key={key}
                      >
                        {property.kind === "COLOR" ? (
                          <span className="flex items-center space-x-1">
                            <span>{getOptionLabel(locale, option)}</span>
                            <span
                              className="inline-block h-4 w-4 rounded-xs"
                              style={{ backgroundColor: option.hex ?? "" }}
                            />
                          </span>
                        ) : (
                          getOptionLabel(locale, option)
                        )}
                      </span>
                    );
                  })}
                </div>
              )}
              {isExpanded && (
                <hr className="my-2 ml-3 w-[calc(100%-12px)] bg-gray-200 text-gray-200" />
              )}

              {/* Title */}
              {isExpanded && title ? (
                <p
                  className={clsx(
                    "font-semibold ltr:mr-3 ltr:pl-3 rtl:ml-5 rtl:pr-3",
                    isExpanded ? "text-base lg:text-lg" : "text-sm",
                  )}
                >
                  {title}
                </p>
              ) : null}

              {/* Description */}
              {isExpanded && description ? (
                <p
                  className={clsx(
                    "max-h-40 text-gray-700 lg:text-lg ltr:mr-3 ltr:pl-3 ltr:leading-6 lg:ltr:leading-8 rtl:ml-3 rtl:pr-3 rtl:text-justify rtl:leading-7 lg:rtl:leading-9 lg:rtl:tracking-tight",
                  )}
                >
                  {description}
                </p>
              ) : null}

              {/* CTA only when expanded */}
              {isExpanded && href ? (
                <motion.div
                  className="mt-12 ml-3 flex flex-wrap gap-2"
                  dir="ltr"
                >
                  <Link
                    href={href}
                    className="bg-pinkCustom inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {viewLabel}
                  </Link>
                </motion.div>
              ) : null}
            </div>
          </div>
        )}
      </ExpandableTeaserCard>
    </div>
  );
}

type PreviewCardProps = {
  product: ProductListItem;
  locale: Locale;
  prefetchedProduct?: ProductTeaser;
  copy: {
    loading: string;
  };
};

const PreviewCard: React.FC<PreviewCardProps> = ({
  product,
  locale,
  prefetchedProduct,
  copy,
}) => {
  const fullProduct = prefetchedProduct ?? null;

  const partNumber =
    fullProduct?.part_number ??
    product.partNumber ??
    product.productId ??
    "Product";

  const slug =
    fullProduct?.slug ??
    fullProduct?.part_number ??
    product.partNumber ??
    product.productId ??
    "";
  const href = slug
    ? `/${locale}/product/${encodeURIComponent(slug)}`
    : undefined;

  const properties = (fullProduct?.productProperty ?? []).filter(
    isPropertyWithMeta,
  );
  const showLoadingProperties =
    properties.length === 0 && Boolean(product.productId) && !prefetchedProduct;

  const heroSrc =
    fullProduct?.heroAsset?.url || fullProduct?.hero_image || FALLBACK_IMAGE;

  const categorySrc =
    fullProduct?.categoryAsset?.url || fullProduct?.category_image;

  const body = (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      <div className="flex items-start gap-3 rounded-md">
        <div className="relative aspect-video w-26 overflow-hidden rounded-md bg-white">
          {categorySrc ? (
            <Image
              src={categorySrc}
              alt={partNumber}
              fill
              sizes="80px"
              className="object-cover"
            />
          ) : (
            <FarbinPlaceholderSVG className="h-full w-full p-3" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-0 lg:gap-2" dir="ltr">
          <p className="pt-1 text-base font-semibold text-gray-900 lg:text-lg">
            {partNumber}
          </p>
          <div
            className={clsx(
              "flex h-12 w-fit flex-wrap items-start gap-1.5 gap-y-2 pt-2 pr-2 text-gray-700 lg:pt-0",
            )}
          >
            {showLoadingProperties ? (
              <span className="text-gray-500">{copy.loading}</span>
            ) : (
              properties.map((p) => {
                const { property, option, optionId, propertyId } = p;
                const key = `${propertyId}-${optionId}`;
                return (
                  <span
                    className={clsx(
                      "inline-flex items-center gap-1 border-r border-gray-300 text-xs text-gray-800 last:border-r-0 lg:text-sm ltr:pr-1.5",
                    )}
                    key={key}
                    dir={locale === "fa" ? "rtl" : "ltr"}
                  >
                    {property.kind === "COLOR" ? (
                      <>
                        <span>{getOptionLabel(locale, option)}</span>
                        <span
                          className="inline-block h-3.5 w-3.5 -translate-y-0.5 rounded-xs"
                          style={{ backgroundColor: option.hex ?? "" }}
                        />
                      </>
                    ) : (
                      getOptionLabel(locale, option)
                    )}
                  </span>
                );
              })
            )}
          </div>
        </div>
      </div>
      <div className="relative aspect-video w-full overflow-hidden rounded-md bg-white">
        <Image
          src={heroSrc}
          alt={partNumber}
          fill
          sizes="(min-width: 1024px) 360px, 100vw"
          className="object-cover"
        />
      </div>
    </div>
  );

  return href ? (
    <Link href={href} className="block h-full">
      {body}
    </Link>
  ) : (
    body
  );
};

export const ProductListTeaser: ContentTeaserComponent = ({
  blocks,
  locale,
  viewMode,
  title,
  contentId,
  onRequestEdit,
  showEditButton,
}) => {
  const headings = useMemo(
    () => findBlocks(blocks, "heading") as HeadingBlock[],
    [blocks],
  );
  const productCards = useMemo(
    () => findBlocks(blocks, "productCard") as ProductCardBlock[],
    [blocks],
  );
  const heading = headings[0];
  const productCard = productCards[0];
  const products = normalizeProductList(productCard);
  const listGroups = useMemo(
    () =>
      productCards
        .map((card, index) => ({
          heading: headings[index],
          products: normalizeProductList(card),
        }))
        .filter((group) => group.products.length > 0),
    [headings, productCards],
  );
  const effectiveViewMode = viewMode ?? "list";
  const renderAsCarousel = effectiveViewMode === "carousel";
  const renderAsCard = effectiveViewMode === "card";
  const renderAsPreviewCard = effectiveViewMode === "previewCard";
  const productsForPrefetch = useMemo(
    () =>
      effectiveViewMode === "list"
        ? listGroups.flatMap((group) => group.products)
        : products,
    [effectiveViewMode, listGroups, products],
  );
  const productIds = useMemo(
    () =>
      Array.from(
        new Set(
          productsForPrefetch
            .map((p) => p.productId)
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [productsForPrefetch],
  );
  const { products: prefetchedProducts } = useProductTeasers(productIds);
  const isRTL = locale === "fa";
  const copy = {
    close: isRTL ? "بستن" : "Close",
    loading: isRTL ? "در حال بارگذاری..." : "Loading details...",
    error: isRTL
      ? "امکان بارگذاری جزئیات نیست"
      : "Could not load product details",
  };
  const headingText = heading?.data?.text ?? null;
  const listTitle =
    title ?? headingText ?? (isRTL ? "لیست محصولات" : "Product list");

  const canShowEdit = Boolean(showEditButton && contentId && onRequestEdit);

  if (renderAsCarousel) {
    if (!products.length) return null;

    return (
      <ProductCarousel
        heading={heading}
        titleText={listTitle}
        contentId={contentId}
        onRequestEdit={onRequestEdit}
        showEditButton={showEditButton}
        products={products}
        prefetchedProducts={prefetchedProducts}
        locale={locale}
      />
    );
  }

  if (renderAsCard) {
    if (!products.length) return null;

    return (
      <article className="relative rounded-lg lg:h-full">
        {listTitle ? (
          <header className="mb-4" dir={isRTL ? "rtl" : "ltr"}>
            <h2 className="relative flex justify-between text-xl font-bold lg:text-2xl ltr:ml-4 ltr:lg:mx-0 rtl:mr-4">
              {listTitle}
              {canShowEdit ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRequestEdit!(contentId!);
                  }}
                  className="mr-2 inline-flex h-7 w-7 items-center justify-center rounded-md align-middle"
                  aria-label="Edit content"
                  title="Edit content"
                >
                  <EditSVG
                    className="h-5 w-5"
                    svgColor="pinkCustom"
                    accentColor="var(--color-gray-500)"
                  />
                </button>
              ) : null}
              <div className={`absolute top-5 ltr:-left-3.5 rtl:-right-3.5`}>
                <Image
                  className="w-3 rtl:-rotate-90"
                  src={arrow2}
                  alt="arrow"
                />
              </div>
            </h2>
          </header>
        ) : null}
        <ul className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-8">
          {products.map((product, index) => (
            <li
              className="bg-grayCustom rounded-lg"
              key={
                product.productId ??
                product.partNumber ??
                `card-product-${index}`
              }
            >
              <TeaserProductRow
                product={product}
                isExpanded
                prefetchedProduct={
                  product.productId
                    ? prefetchedProducts[product.productId]
                    : undefined
                }
                variant="single"
                locale={locale}
                copy={copy}
                headingLabel={headingText}
              />
            </li>
          ))}
        </ul>
      </article>
    );
  }

  if (renderAsPreviewCard) {
    if (!products.length) return null;

    const columnCount =
      products.length % 3 === 0
        ? 3
        : products.length % 2 === 0
          ? 2
          : products.length >= 3
            ? 3
            : 2;
    const gridColsClass =
      columnCount === 3 ? "md:grid-cols-3" : "md:grid-cols-2";

    return (
      <article className="relative rounded-lg lg:h-full">
        {listTitle ? (
          <header className="mb-4" dir={isRTL ? "rtl" : "ltr"}>
            <h2 className="relative flex justify-between text-xl font-bold lg:text-2xl ltr:ml-4 ltr:lg:mx-0 rtl:mr-4">
              {listTitle}
              {canShowEdit ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRequestEdit!(contentId!);
                  }}
                  className="mr-2 inline-flex h-7 w-7 items-center justify-center rounded-md align-middle"
                  aria-label="Edit content"
                  title="Edit content"
                >
                  <EditSVG
                    className="h-5 w-5"
                    svgColor="pinkCustom"
                    accentColor="var(--color-gray-500)"
                  />
                </button>
              ) : null}
              <div className={`absolute top-5 ltr:-left-3.5 rtl:-right-3.5`}>
                <Image
                  className="w-3 rtl:-rotate-90"
                  src={arrow2}
                  alt="arrow"
                />
              </div>
            </h2>
          </header>
        ) : null}

        <ul className={clsx("grid grid-cols-1 gap-10 lg:gap-6", gridColsClass)}>
          {products.map((product, index) => (
            <li
              className="h-full"
              key={
                product.productId ??
                product.partNumber ??
                `preview-card-product-${index}`
              }
            >
              <PreviewCard
                product={product}
                locale={locale}
                prefetchedProduct={
                  product.productId
                    ? prefetchedProducts[product.productId]
                    : undefined
                }
                copy={copy}
              />
            </li>
          ))}
        </ul>
      </article>
    );
  }

  if (!listGroups.length) return null;

  return (
    <div dir="ltr" className="h-full">
      {listTitle ? (
        <header className="mb-3" dir={isRTL ? "rtl" : "ltr"}>
          <h2 className="relative flex justify-between text-xl font-bold lg:text-2xl ltr:ml-4 ltr:lg:mx-0 rtl:mr-4">
            {listTitle}
            {canShowEdit ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onRequestEdit!(contentId!);
                }}
                className="mr-2 inline-flex h-7 w-7 items-center justify-center rounded-md align-middle"
                aria-label="Edit content"
                title="Edit content"
              >
                <EditSVG
                  className="h-5 w-5"
                  svgColor="pinkCustom"
                  accentColor="var(--color-gray-500)"
                />
              </button>
            ) : null}
            <div
              className={`absolute top-5 ltr:-left-3.5 rtl:top-7 rtl:-right-3.5`}
            >
              <Image className="w-3 rtl:-rotate-90" src={arrow2} alt="arrow" />
            </div>
          </h2>
        </header>
      ) : null}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-8">
        {listGroups.map((group, index) => {
          const groupHeading = group.heading?.data?.text ?? null;
          const groupProducts = group.products;
          const groupCanExpand =
            !renderAsCard && !renderAsCarousel && groupProducts.length > 4;
          const groupPreview = groupCanExpand
            ? groupProducts.slice(0, 4)
            : groupProducts;

          return (
            <ExpandableTeaserCard
              key={`product-group-${index}`}
              canExpand={groupCanExpand}
              recalcOn={groupProducts.length}
              className={clsx(
                "top-4 h-full space-y-3 bg-white p-2 pt-5 pr-0 lg:px-6 lg:pr-0",
              )}
              expandedMaxWidth={
                window.innerWidth > 1024 ? "750px" : "100% - 32px"
              }
            >
              {({ isExpanded, handleCollapse }) => {
                const visibleProducts = isExpanded
                  ? groupProducts
                  : groupPreview;

                return (
                  <React.Fragment>
                    <div
                      className={clsx(
                        "flex w-full items-start justify-between gap-4",
                        isRTL ? "pl-4" : "pr-4",
                      )}
                      dir={isRTL ? "rtl" : "ltr"}
                    >
                      {groupHeading ? (
                        <h2
                          className={clsx(
                            "pr-2 text-xl font-bold text-gray-900 lg:pr-6 lg:text-[26px]",
                          )}
                        >
                          {groupHeading}
                        </h2>
                      ) : null}
                      {isExpanded ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCollapse();
                          }}
                          aria-label={copy.close}
                          className="bg-grayCustom text-slateCustom hover:text-pinkCustom flex h-9 w-9 items-center justify-center rounded-full transition"
                        >
                          <X className="h-4 w-4" strokeWidth={4} />
                        </button>
                      ) : null}
                    </div>
                    <motion.div
                      className={`relative ${
                        isExpanded ? "w-full overflow-hidden" : ""
                      }`}
                    >
                      <div
                        className={
                          isExpanded
                            ? clsx(
                                "h-full overflow-y-auto",
                                isRTL ? "pl-1" : "pr-1",
                              )
                            : undefined
                        }
                      >
                        <motion.ul>
                          {visibleProducts.map((product, productIndex) => (
                            <React.Fragment
                              key={
                                product.productId ??
                                product.partNumber ??
                                `product-${productIndex}`
                              }
                            >
                              <TeaserProductRow
                                product={product}
                                isExpanded={isExpanded}
                                prefetchedProduct={
                                  product.productId
                                    ? prefetchedProducts[product.productId]
                                    : undefined
                                }
                                locale={locale}
                                copy={copy}
                              />
                              {productIndex < visibleProducts.length - 1 && (
                                <hr className="ml-auto h-px w-[calc(100%-112px)] bg-gray-200 text-gray-200 lg:w-[calc(100%-140px)]" />
                              )}
                            </React.Fragment>
                          ))}
                        </motion.ul>
                      </div>
                    </motion.div>
                  </React.Fragment>
                );
              }}
            </ExpandableTeaserCard>
          );
        })}
      </div>
    </div>
  );
};
